import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProduct } from '@/lib/hooks';
import { useCart } from '@/context/CartContext';
import { useLanguage } from '@/context/LanguageContext';
import { useSEO, generateBreadcrumbStructuredData } from '@/lib/useSEO';
import * as analytics from '@/lib/analytics';
import { Container, Grid, Image, Title, Text, Button, Group, ActionIcon, Badge, Alert, Loader, Box, Divider, Table, Tabs, List, Modal, ScrollArea } from '@mantine/core';
import { IconMinus, IconPlus, IconArrowLeft, IconZoomIn } from '@tabler/icons-react';

export default function ProductPage() {
  const params = useParams();
  const navigate = useNavigate();
  const { addItem, getProductQuantity } = useCart();
  const { language, t } = useLanguage();
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [zoomModalOpen, setZoomModalOpen] = useState(false);
  const [zoom] = useState(1.5);

  const slug = params?.slug as string;
  const { data, isLoading: loading } = useProduct(slug);
  const product = data?.product ?? null;
  const cartQuantity = product ? getProductQuantity(product.id) : 0;

  // Get tab value based on current language
  const getTabValue = (tabKey: string) => {
    const tabMap: Record<string, Record<string, string>> = {
      'description': { en: 'description', pl: 'opis', de: 'beschreibung', fr: 'description', es: 'descripcion', it: 'descrizione' },
      'specification': { en: 'specification', pl: 'specyfikacja', de: 'spezifikation', fr: 'specification', es: 'especificacion', it: 'specifica' },
      'applications': { en: 'applications', pl: 'zastosowanie', de: 'anwendungen', fr: 'applications', es: 'aplicaciones', it: 'applicazioni' },
      'documents': { en: 'documents', pl: 'dokumenty', de: 'dokumente', fr: 'documents', es: 'documentos', it: 'documenti' },
      'tests': { en: 'tests', pl: 'testy', de: 'tests', fr: 'tests', es: 'pruebas', it: 'test' }
    };
    return tabMap[tabKey]?.[language] || tabKey;
  };

  // Get current language content
  const getContent = () => {
    if (!product) return null;
    const langMap: Record<string, string> = {
      'en': 'en',
      'pl': 'pl',
      'de': 'de',
      'fr': 'fr',
      'es': 'es',
      'it': 'it'
    };
    const productLang = langMap[language] || 'en';
    return product.translations?.[productLang] || product.translations?.['en'] || {
      shortDescription: product.shortDescription,
      description: product.description,
      applications: [],
      documents: [],
      tests: []
    };
  };

  // Initialize activeTab based on current language
  useEffect(() => {
    setActiveTab(getTabValue('description'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  // Track product view when product loads
  useEffect(() => {
    if (product) {
      analytics.viewItem({
        item_id: product.id,
        item_name: product.name,
        price: product.price,
      });
    }
  }, [product]);

  // SEO - Get product-specific content for current language
  const productSEOContent = product ? (product.translations?.[language] || product.translations?.['en'] || {}) : null;
  const productName = product?.nameTranslations?.[language] || product?.name || '';
  const productDescription = productSEOContent?.shortDescription || productSEOContent?.description || product?.shortDescription || '';
  const productImage = product?.images?.[0] || undefined;

  // Breadcrumb structured data
  const breadcrumbData = product ? generateBreadcrumbStructuredData([
    { name: t('nav.home'), url: 'https://luxelo.web.app/' },
    { name: t('nav.products'), url: 'https://luxelo.web.app/products' },
    { name: productName, url: `https://luxelo.web.app/product/${product.slug}` },
  ]) : undefined;

  // Apply SEO
  useSEO({
    title: productName || t('product.loading'),
    description: productDescription || t('seo.defaultDescription'),
    image: productImage,
    type: 'product',
    product: product || undefined,
    structuredData: breadcrumbData,
  });

  const handleAddToCart = () => {
    if (product) {
      addItem(product, quantity);
    }
  };

  if (loading) {
    return (
      <Container py="xl">
        <Loader size="lg" style={{ display: 'block', margin: '0 auto' }} />
      </Container>
    );
  }

  if (!product) {
    return (
      <Container py="xl">
        <Alert color="red" title={t('product.notFound')}>
          <Button leftSection={<IconArrowLeft size={16} />} onClick={() => navigate('/')} mt="md">
            {t('product.backToHome')}
          </Button>
        </Alert>
      </Container>
    );
  }

  const content = getContent();
  const photos = product.photos || (product.images?.map((img: string) => ({ thumb: img, full: img })) || []);
  const mainPhoto = photos[selectedImage]?.full || photos[selectedImage]?.thumb || product.images?.[selectedImage];

  return (
    <Container size="xl" py="xl">
      <Modal
        opened={zoomModalOpen}
        onClose={() => setZoomModalOpen(false)}
        size="auto"
        centered
        padding={0}
        styles={{
          body: { padding: 0 },
          content: { width: '90vw', maxWidth: '600px', aspectRatio: '1 / 1' }
        }}
      >
        <ScrollArea
          style={{
            width: '100%',
            aspectRatio: '1 / 1',
            backgroundColor: '#000',
          }}
          type="scroll"
          scrollbarSize={8}
        >
          <Box
            style={{
              width: `${zoom * 100}%`,
              height: `${zoom * 100}%`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '100%',
              minWidth: '100%',
            }}
          >
            <Image
              src={mainPhoto}
              alt={product.name}
              fit="contain"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                width: 'auto',
                height: 'auto',
                display: 'block',
              }}
            />
          </Box>
        </ScrollArea>
      </Modal>

      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Box style={{ position: 'relative', cursor: 'pointer' }} onClick={() => setZoomModalOpen(true)}>
            <Image
              src={mainPhoto}
              alt={product.name}
              radius="md"
              fallbackSrc="https://via.placeholder.com/600"
            />
            <ActionIcon
              variant="filled"
              size="lg"
              radius="md"
              style={{
                position: 'absolute',
                top: 10,
                right: 10,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
              }}
              onClick={(e) => {
                e.stopPropagation();
                setZoomModalOpen(true);
              }}
            >
              <IconZoomIn size={20} />
            </ActionIcon>
          </Box>
          {photos.length > 1 && (
            <ScrollArea mt="md" type="scroll" scrollbarSize={8} style={{ width: '100%' }}>
              <Group gap="xs" style={{ display: 'flex', flexWrap: 'nowrap', width: 'max-content' }}>
                {photos.map((photo: { thumb?: string; full?: string }, index: number) => {
                  const thumbSrc = photo.thumb || photo.full;
                  return (
                    <Box
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      style={{
                        cursor: 'pointer',
                        flexShrink: 0,
                        width: 80,
                        height: 80,
                        border: selectedImage === index ? '2px solid #228be6' : '1px solid #dee2e6',
                        borderRadius: '4px',
                        opacity: selectedImage === index ? 1 : 0.7,
                        overflow: 'hidden',
                        transition: 'opacity 0.2s, border-color 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        if (selectedImage !== index) {
                          e.currentTarget.style.opacity = '0.9';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedImage !== index) {
                          e.currentTarget.style.opacity = '0.7';
                        }
                      }}
                    >
                      <Image
                        src={thumbSrc}
                        alt={`${product.name} ${index + 1}`}
                        width={80}
                        height={80}
                        fit="cover"
                        style={{ width: '100%', height: '100%', display: 'block' }}
                      />
                    </Box>
                  );
                })}
              </Group>
            </ScrollArea>
          )}
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 6 }}>
          <Title order={1} mb="md">{product.name}</Title>
          <Box mb="md">
            <Text size="xl" fw={700} c="dark">
              {product.price.toFixed(2)} PLN <Text component="span" size="sm" fw={500} c="dimmed">{t('product.gross') || 'brutto'}</Text>
            </Text>
            <Text size="sm" c="dimmed">
              {(product.price / 1.23).toFixed(2)} PLN {t('product.net') || 'netto'}
            </Text>
          </Box>
          
          {content && content.shortDescription && (
            <Text size="md" c="dimmed" mb="lg" style={{ fontWeight: 500 }}>
              {content.shortDescription}
            </Text>
          )}

          {product.inStock ? (
            <>
              <Group mb="xl">
                <Text fw={600}>{t('product.quantity')}:</Text>
                <ActionIcon
                  variant="light"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                >
                  <IconMinus size={16} />
                </ActionIcon>
                <Text fw={600} style={{ minWidth: '40px', textAlign: 'center' }}>
                  {quantity}
                </Text>
                <ActionIcon
                  variant="light"
                  onClick={() => setQuantity(quantity + 1)}
                >
                  <IconPlus size={16} />
                </ActionIcon>
              </Group>

              <Group gap="md" mb="md">
                <Button size="lg" onClick={handleAddToCart} style={{ flex: 1 }}>
                  {t('product.addToCart')}
                </Button>
                {cartQuantity > 0 && (
                  <Badge color="dark" size="xl" variant="filled" style={{ minWidth: '100px' }}>
                    {cartQuantity} {t('product.inCart')}
                  </Badge>
                )}
              </Group>
            </>
          ) : (
            <Badge color="red" size="lg" variant="light" fullWidth py="md">
              {t('product.outOfStock')}
            </Badge>
          )}
        </Grid.Col>
      </Grid>

      <Divider my="xl" />
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value={getTabValue('description')}>{t('product.tabs.description')}</Tabs.Tab>
          <Tabs.Tab value={getTabValue('specification')}>{t('product.tabs.specification')}</Tabs.Tab>
          <Tabs.Tab value={getTabValue('applications')}>{t('product.tabs.applications')}</Tabs.Tab>
          <Tabs.Tab value={getTabValue('documents')}>{t('product.tabs.documents')}</Tabs.Tab>
          <Tabs.Tab value={getTabValue('tests')}>{t('product.tabs.tests')}</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value={getTabValue('description')} pt="md">
          <Box>
            {content && content.description && (
              <Text size="md" style={{ whiteSpace: 'pre-line' }}>
                {content.description}
              </Text>
            )}
          </Box>
        </Tabs.Panel>

        <Tabs.Panel value={getTabValue('specification')} pt="md">
          <Box>
            {product.attributes && Object.keys(product.attributes).length > 0 ? (
              <Table striped highlightOnHover withTableBorder withColumnBorders>
                <Table.Tbody>
                  {Object.entries(product.attributes).map(([key, attrValue]) => {
                    const attr = attrValue as { label: string; value: string; unit?: string };
                    return (
                      <Table.Tr key={key}>
                        <Table.Td fw={600} style={{ width: '40%' }}>
                          {attr.label}
                        </Table.Td>
                        <Table.Td>
                          {attr.value}{attr.unit ? ` ${attr.unit}` : ''}
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            ) : (
              <Text c="dimmed">{t('product.noSpecifications')}</Text>
            )}
          </Box>
        </Tabs.Panel>

        <Tabs.Panel value={getTabValue('applications')} pt="md">
          <Box>
            {content && content.applications && content.applications.length > 0 ? (
              <List spacing="md" size="md">
                {content.applications.map((app: string, index: number) => (
                  <List.Item key={index}>{app}</List.Item>
                ))}
              </List>
            ) : (
              <Text c="dimmed">{t('product.noApplications')}</Text>
            )}
          </Box>
        </Tabs.Panel>

        <Tabs.Panel value={getTabValue('documents')} pt="md">
          <Box>
            {content && content.documents && content.documents.length > 0 ? (
              <List spacing="md" size="md">
                {content.documents.map((doc: string, index: number) => (
                  <List.Item key={index}>{doc}</List.Item>
                ))}
              </List>
            ) : (
              <Text c="dimmed">{t('product.noDocuments')}</Text>
            )}
          </Box>
        </Tabs.Panel>

        <Tabs.Panel value={getTabValue('tests')} pt="md">
          <Box>
            {content && content.tests && content.tests.length > 0 ? (
              <List spacing="md" size="md">
                {content.tests.map((test: string, index: number) => (
                  <List.Item key={index}>{test}</List.Item>
                ))}
              </List>
            ) : (
              <Text c="dimmed">{t('product.noTests')}</Text>
            )}
          </Box>
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
}

